import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaPlus } from "react-icons/fa";
import {
  ArrowLeft,
  CalendarDays,
  ChevronDown,
  Trash2,
  IndianRupee,
  X,
} from "lucide-react";
import { BASE_URL } from "utils/constant";
import { useAlert } from "components/UI_components/Alert";
import { resolveTransactionFeedback } from "utils/transactionFeedback";

const formatDisplayDate = (isoString) => {
  const date = new Date(isoString);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const GST_SLABS = [0, 5, 8, 12, 18];
export default function Request({ onClose, onSuccess }) {
  const [project, setProject] = useState();
  const [vendor, setVendor] = useState();
  const [linkedRfq, setLinkedRfq] = useState();
  const [deliveryDate, setDeliveryDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [showDatePicker, setShowDatePicker] = useState(false);

  const [paymentMethod, setPaymentMethod] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [paidAmount, setPaidAmount] = useState(0);
  const [remarks, setRemarks] = useState("");
  const [isWarehouse, setIsWarehouse] = useState(false);
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);
  const [showVendorDropdown, setShowVendorDropdown] = useState(false);
  const [showRfqDropdown, setShowRfqDropdown] = useState(false);
  const [showMRDropdown, setShowMRDropdown] = useState(false);
  const [organisationId, setOrganisationId] = useState(
    JSON.parse(localStorage.getItem("organization"))?.id,
  );
  const [materials, setMaterials] = useState(() => []);
  const [customExpenses, setCustomExpenses] = useState([]);
  const [materialLibrary, setMaterialLibrary] = useState([]);
  const [requests, setRequests] = useState([]);
  const [selectedMR, setSelectedMR] = useState(null);
  const [rfqs, setRfqs] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [sites, setSites] = useState([]);
  const [quotations, setQuotations] = useState([]);
  const [showQuotationDropdown, setShowQuotationDropdown] = useState(false);
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [showAIModal, setShowAIModal] = useState(false);
  const [billDocument, setBillDocument] = useState(null);
  const [isProcessingBill, setIsProcessingBill] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [unmatchedItems, setUnmatchedItems] = useState([]);
  const { addAlert } = useAlert();
  const projectRef = useRef(null);
  const vendorRef = useRef(null);
  const mrRef = useRef(null);
  const rfqRef = useRef(null);
  const quotationRef = useRef(null);
  const addMaterial = () => {
    setMaterials((prev) => [
      ...prev,
      {
        name: "Select Material",
        qty: "",
        unit: "Unit",
        rate: "",
        tax_amount: 0,
        total_price: 0,
        showGstInput: false,
        gst_percentage: 0,
      },
    ]);
  };

  const updateMaterial = (index, updates) => {
    setMaterials((prev) =>
      prev.map((m, i) => (i === index ? { ...m, ...updates } : m)),
    );
  };

  const removeMaterial = (index) => {
    setMaterials((prev) => prev.filter((_, i) => i !== index));
  };

  const addCustomExpense = () => {
    setCustomExpenses((prev) => [...prev, { label: "", amount: 0 }]);
  };

  const updateCustomExpense = (index, updates) => {
    setCustomExpenses((prev) =>
      prev.map((c, i) => (i === index ? { ...c, ...updates } : c)),
    );
  };

  const removeCustomExpense = (index) => {
    setCustomExpenses((prev) => prev.filter((_, i) => i !== index));
  };

  const materialTotals = useMemo(() => {
    return materials.map((m) => {
      const base = Number(m.qty) * Number(m.rate);
      return m.tax_amount ? base + Number(m.tax_amount) : base;
    });
  }, [materials]);

  const materialsSubtotal = useMemo(
    () => materialTotals.reduce((a, b) => a + b, 0),
    [materialTotals],
  );
  const customTotal = useMemo(
    () => customExpenses.reduce((sum, e) => sum + Number(e.amount || 0), 0),
    [customExpenses],
  );
  const grandTotal = useMemo(
    () => materialsSubtotal + customTotal,
    [materialsSubtotal, customTotal],
  );
  const remainingPayment = useMemo(
    () => Math.max(grandTotal - Number(paidAmount || 0), 0),
    [grandTotal, paidAmount],
  );

  // ─── Reactive form validity: button brightens only when ALL required fields are filled ───
  const isFormValid = useMemo(() => {
    // 1. Project or warehouse
    if (!isWarehouse && !project) return false;
    // 2. Vendor
    if (!vendor) return false;
    // 3. At least one material
    if (materials.length === 0) return false;
    // 4. Every material must have a selected id, valid qty > 0, valid rate > 0
    const allMaterialsValid = materials.every(
      (m) =>
        m.id && m.id !== "" && parseFloat(m.qty) > 0 && parseFloat(m.rate) > 0,
    );
    if (!allMaterialsValid) return false;
    // 5. Payment method selected
    if (!paymentMethod) return false;
    // 6. Payment status selected
    if (!paymentStatus) return false;
    return true;
  }, [isWarehouse, project, vendor, materials, paymentMethod, paymentStatus]);

  const getMaterials = async () => {
    try {
      const res = await fetch(`${BASE_URL}/org/material-library/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ org_id: organisationId }),
      });

      if (!res.ok) {
        throw new Error("Failed to fetch materials");
      }

      const data = await res.json();
      console.log("Fetched Materials:", data);

      setMaterialLibrary(data);
    } catch (err) {
      console.error("Error fetching materials:", err);
    }
  };
  const getMaterialRequests = async () => {
    try {
      const res = await fetch(
        `${BASE_URL}/inventory/material-requests/org/${organisationId}/`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access")}`,
          },
        },
      );

      if (!res.ok) {
        throw new Error("Failed to fetch material requests");
      }

      const data = await res.json();
      const mrs = [];
      data.forEach((mr) => {
        if (
          (mr.status === "DRAFT" || mr.status === "OPEN") &&
          mr.material_request_lines.length > 0 &&
          mr.material_request_lines.some((line) => line.status === "DRAFT")
        ) {
          if (project && mr.sites !== project.id) {
            return;
          }
          mrs.push({
            ...mr,
            code: mr.mr_number,
            items: mr.material_request_lines.filter(
              (line) => line.status === "DRAFT",
            ).length,
            raisedBy: mr.created_by,
            expectedBy: mr.due_date,
          });
        }
      });
      setRequests(mrs);
    } catch (err) {
      console.error("Error fetching material requests:", err);
    }
  };
  const getRfqs = async () => {
    try {
      const res = await fetch(
        `${BASE_URL}/inventory/rfq/org/${organisationId}/`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access")}`,
          },
        },
      );
      if (!res.ok) {
        throw new Error("Failed to fetch RFQs");
      }
      const data = await res.json();

      if (selectedMR && selectedMR.id) {
        const filteredRfqs = data.filter(
          (r) => r.material_request === selectedMR.id && r.status === "OPEN",
        );
        setRfqs(filteredRfqs);
      } else {
        setRfqs(data.filter((r) => r.status === "OPEN"));
      }
    } catch (err) {
      console.error("Error fetching RFQs:", err);
    }
  };
  const fetchVendors = async () => {
    try {
      const token = localStorage.getItem("access");
      const response = await fetch(
        `${BASE_URL}/inventory/list-org-vendors/${organisationId}/`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const result = await response.json();

      if (response.ok && result.status && Array.isArray(result.data)) {
        setVendors(result.data);
      } else {
        console.error("Failed to load vendors", result.message);
      }
    } catch (error) {
      console.error("Error fetching vendors:", error);
    }
  };
  const getSites = async () => {
    const res = await fetch(
      `${BASE_URL}/sites/organizations/${organisationId}/sites/`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access")}`,
        },
      },
    );
    const data = await res.json();
    setSites(Array.isArray(data) ? data : []);
  };
  const getQuotations = async () => {
    if (!linkedRfq) {
      setQuotations([]);
      return;
    }

    try {
      const res = await fetch(
        `${BASE_URL}/inventory/applications/rfq/${linkedRfq?.id}/`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access")}`,
          },
        },
      );
      if (!res.ok) {
        throw new Error("Failed to fetch quotations");
      }
      const data = await res.json();
      console.log("Fetched Quotations:", data);
      setQuotations(data);
    } catch (err) {
      console.error("Error fetching quotations:", err);
    }
  };

  useEffect(() => {
    getSites();
    getMaterials();
    fetchVendors();
  }, []);
  useEffect(() => {
    getMaterialRequests();
  }, [project]);

  useEffect(() => {
    getRfqs();
  }, [selectedMR]);

  useEffect(() => {
    getQuotations();
  }, [linkedRfq]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (projectRef.current && !projectRef.current.contains(event.target)) {
        setShowProjectDropdown(false);
      }
      if (vendorRef.current && !vendorRef.current.contains(event.target)) {
        setShowVendorDropdown(false);
      }
      if (mrRef.current && !mrRef.current.contains(event.target)) {
        setShowMRDropdown(false);
      }
      if (rfqRef.current && !rfqRef.current.contains(event.target)) {
        setShowRfqDropdown(false);
      }
      if (
        quotationRef.current &&
        !quotationRef.current.contains(event.target)
      ) {
        setShowQuotationDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleQuotationSelect = (quotation) => {
    setSelectedQuotation(quotation);
    setShowQuotationDropdown(false);
    const updatedMaterials = quotation.application_lines.map((line) => ({
      id: line.material,
      name: line.material_name,
      qty: line.quantity,
      unit: line.uom,
      rate: line.unit_price,
      total_price: line.total_price,
      tax_amount: line.tax_amount,
      application_lines: line.id,
      mr_lines: line?.mr_lines,
      rfq_lines: line?.rfq_lines,
      gst_percentage:
        (line.tax_amount / (line.quantity * line.unit_price)) * 100 || 0,
      showGstInput: Boolean(line.tax_amount > 0),
    }));
    setMaterials(updatedMaterials);
    setDeliveryDate(quotation.delivery_date);
    setVendor({
      id: quotation.vendor,
      name: quotation.vendor_name,
      contact_no: quotation.phone,
    });
  };
  const handleMaterialRequestSelect = (mr) => {
    setSelectedMR(mr);
    setLinkedRfq(null);
    setSelectedQuotation(null);
    setShowMRDropdown(false);
    const updatedMaterials = mr.material_request_lines
      .filter((line) => line.status === "DRAFT")
      .map((line) => ({
        id: line.material,
        name: line.material_name,
        qty: line.quantity,
        unit: line.uom,
        rate: "",
        addGst: false,
        tax_amount: 0,
        total_price: 0,
        mr_lines: line.id,
        showGstInput: false,
        gst_percentage: 0,
      }));
    setMaterials(updatedMaterials);
    setProject({
      id: mr.sites,
      site_name: mr?.site_name,
    });
  };
  const handleRFQSelect = (rfq) => {
    setLinkedRfq(rfq);
    setSelectedQuotation(null);
    setShowRfqDropdown(false);
    if (rfq?.sites) {
      setProject({
        id: rfq.sites,
        site_name: rfq?.site_name,
      });
    } else {
      setProject(null);
    }
    if (rfq && rfq.lines) {
      const updatedMaterials = rfq.lines.map((line) => ({
        id: line.material,
        name: line.material_name,
        qty: line.quantity,
        unit: line.uom,
        addGst: false,
        rate: "",
        tax_amount: 0,
        total_price: 0,
        rfq_lines: line.id,
        mr_lines: line?.mr_lines,
        showGstInput: false,
        gst_percentage: 0,
      }));
      setMaterials(updatedMaterials);
    }
  };

  const toggleGstInput = (index) => {
    setMaterials(
      materials.map((m, i) => {
        if (i !== index) return m;

        if (m.showGstInput) {
          return {
            ...m,
            showGstInput: false,
            gst_percentage: 0,
            tax_amount: 0,
            total_price: m.qty * m.rate,
          };
        }

        const qty = parseFloat(m.qty) || 0;
        const rate = parseFloat(m.rate) || 0;
        const taxAmount = parseFloat(m.tax_amount) || 0;

        let initialGstPercent = 0;
        if (qty > 0 && rate > 0 && taxAmount > 0) {
          initialGstPercent = (taxAmount / (qty * rate)) * 100;
          initialGstPercent = GST_SLABS.reduce((prev, curr) =>
            Math.abs(curr - initialGstPercent) <
              Math.abs(prev - initialGstPercent)
              ? curr
              : prev,
          );
        }

        return {
          ...m,
          showGstInput: true,
          gst_percentage: initialGstPercent,
          total_price: qty * rate + taxAmount,
        };
      }),
    );
  };

  const validateForm = () => {
    const errors = [];

    if (!isWarehouse && !project) {
      errors.push("Please select a project or mark as warehouse order");
    }

    if (!vendor) {
      errors.push("Please select a vendor");
    }

    if (materials.length === 0) {
      errors.push("Please add at least one material");
    } else {
      materials.forEach((m, idx) => {
        if (!m.id || m.id === "") {
          errors.push(`Material ${idx + 1}: Please select a material`);
        }
        if (!m.qty || parseFloat(m.qty) <= 0) {
          errors.push(`Material ${idx + 1}: Please enter a valid quantity`);
        }
        if (!m.rate || parseFloat(m.rate) <= 0) {
          errors.push(`Material ${idx + 1}: Please enter a valid rate`);
        }
      });
    }

    return errors;
  };

  const handleApprove = async () => {
    const validationErrors = validateForm();

    if (validationErrors.length > 0) {
      validationErrors.forEach((error) => {
        addAlert(error, "error");
      });
      return;
    }

    if (!paymentMethod) {
      addAlert("Please select a Payment Method", "error");
      return;
    }
    if (!paymentStatus) {
      addAlert("Please select a Payment Status", "error");
      return;
    }

    try {
      const res = await fetch(`${BASE_URL}/inventory/purchase-orders/create/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("access")}`,
        },
        body: JSON.stringify({
          organization: organisationId,
          site: project?.id || null,
          material_request: selectedMR?.id || null,
          rfq: linkedRfq?.id || null,
          vendor: vendor?.id || null,
          vendor_name: vendor?.name || null,
          contact_no: vendor?.contact_no || null,
          application_against_quotation: selectedQuotation?.id || null,
          order_date: deliveryDate,
          status: "approved",
          payment_method: paymentMethod,
          payment_status: paymentStatus,
          paid_amount: Number(paidAmount || 0),
          remarks,
          lines: materials.map((m) => ({
            material: m.id,
            mr_lines: m.mr_lines || null,
            rfq_lines: m.rfq_lines || null,
            application_lines: m.application_lines || null,
            quantity: Number(m.qty),
            unit_price: Number(m.rate),
            tax_amount: Number(m.tax_amount || 0),
            status: "approved",
            total_price: Number(m.qty * m.rate + m.tax_amount || 0),
          })),
        }),
      });
      const data = await res.json().catch(() => null);
      const feedback = resolveTransactionFeedback(data, {
        successMessage: "Purchase Order created successfully",
        errorMessage: "Failed to create Purchase Order",
      });

      if (!res.ok || !feedback.success) {
        addAlert(feedback.message, "error");
        return;
      }

      console.log("Purchase Order Created:", data);
      addAlert(feedback.message, feedback.alertType);
      if (onSuccess) {
        onSuccess(data);
      }
      onClose();
    } catch (err) {
      console.error("Error creating Purchase Order:", err);
      addAlert(err.message || "Failed to create Purchase Order", "error");
    }
  };

  const handleAIBillProcess = async () => {
    if (!billDocument) {
      addAlert("Please upload a bill document", "error");
      return;
    }

    setIsProcessingBill(true);

    try {
      const formData = new FormData();
      formData.append("organization_id", organisationId);
      formData.append("document", billDocument);
      formData.append("created_by", localStorage.getItem("email") || "system");
      formData.append("vendor_id", 18);
      formData.append("vendor_name", "Amit Kumar");
      formData.append("vendor_contact", "+919337316233");

      const res = await fetch(
        `${BASE_URL}/inventory/purchase-orders/create-from-document/`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access")}`,
          },
          body: formData,
        },
      );

      if (!res.ok) {
        throw new Error("Failed to process bill document");
      }

      const data = await res.json();
      console.log("AI Extraction Response:", data);

      if (data.extracted_data) {
        setExtractedData(data.extracted_data);

        if (data.purchase_order?.vendor_id) {
          setVendor({
            id: data.purchase_order.vendor_id,
            name: data.purchase_order.vendor_name,
          });
        }

        if (data.extracted_data.date) {
          setDeliveryDate(data.extracted_data.date);
        }

        if (data.purchase_order?.lines) {
          const extractedMaterials = data.purchase_order.lines.map((line) => ({
            id: line.material_id,
            name: line.material_name,
            qty: line.quantity,
            unit: line.uom || "Unit",
            rate: line.unit_price,
            tax_amount: line.tax_amount || 0,
            total_price: line.total_price,
            gst_percentage:
              line.tax_amount > 0
                ? (line.tax_amount / (line.quantity * line.unit_price)) * 100
                : 0,
            showGstInput: line.tax_amount > 0,
          }));
          setMaterials(extractedMaterials);
        }

        if (data.unmatched_items && data.unmatched_items.length > 0) {
          setUnmatchedItems(data.unmatched_items);
        }

        addAlert(
          `Successfully processed bill! ${data.matched_items_count} items matched.`,
          "success",
        );
        setShowAIModal(false);
      }
    } catch (err) {
      console.error("Error processing bill:", err);
      addAlert("Failed to process bill document", "error");
    } finally {
      setIsProcessingBill(false);
    }
  };

  return (
    <div className="absolute inset-0 bg-gray-50 flex flex-col h-full">
      <div className="flex-shrink-0 flex justify-between items-center bg-white p-4 rounded-lg shadow-sm m-4 mb-0">
        <button
          onClick={onClose}
          className="w-8 h-8 rounded-full hover:bg-gray-200 flex items-center justify-center text-gray-600"
        >
          <ArrowLeft size={18} />
        </button>
        <h2 className="text-lg font-semibold text-gray-900">Purchase Order</h2>
        <div className="w-8">
          <button
            onClick={() => {
              setSelectedMR(null);
              setProject(null);
              setVendor(null);
              setLinkedRfq(null);
              setSelectedQuotation(null);
              setMaterials([]);
              setPaymentMethod(null);
              setPaymentStatus(null);
              setPaidAmount(0);
              setRemarks("");
            }}
            className="text-blue-600 text-sm hover:underline"
          >
            Clear
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-2 mt-2">
        <div className="w-full max-w-6xl mx-auto">
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
              {/* Left Column */}
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex justify-start gap-2">
                    <div className="flex items-center text-sm text-[#232323] mb-1">
                      PO ID
                    </div>
                    <div className="inline-flex items-center gap-2 bg-gray-200 px-2 rounded-full text-sm font-medium text-gray-800">
                      PO_PN_01
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={isWarehouse}
                      style={{ accentColor: "#3B0069" }}
                      onChange={(e) => {
                        setIsWarehouse(e.target.checked);
                        if (e.target.checked === true) {
                          setProject(null);
                          setSelectedMR(null);
                          setLinkedRfq(null);
                          setSelectedQuotation(null);
                        }
                      }}
                      className="h-4 w-4 text-[#3B0069] border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-gray-900">
                      Is this order for Warehouse?
                    </label>
                  </div>
                  {!isWarehouse && (
                    <div ref={projectRef}>
                      <div className="text-sm text-[#232323] mb-1">
                        Project Associated
                      </div>
                      <div
                        className={`h-10 px-3 bg-gray-200 cursor-pointer rounded-md text-sm text-[${isWarehouse ? "#A1A1A1" : "#232323"
                          }] flex items-center justify-between`}
                        onClick={() =>
                          !isWarehouse &&
                          setShowProjectDropdown(!showProjectDropdown)
                        }
                        aria-disabled={isWarehouse}
                        style={{
                          cursor: isWarehouse ? "not-allowed" : "pointer",
                        }}
                      >
                        {project?.site_name || "Select Project"}
                        <span className="text-[#232323]">
                          <ChevronDown size={18} />
                        </span>
                      </div>
                      {showProjectDropdown && (
                        <div className="relative mt-1 w-72 bg-white border border-gray-300 rounded-md shadow-lg z-10 max-h-60 overflow-y-auto">
                          {sites.length === 0 ? (
                            <div className="p-3 text-sm text-gray-500">
                              No projects available
                            </div>
                          ) : (
                            sites.map((site) => (
                              <div
                                key={site.id}
                                onClick={() => {
                                  setProject(site);
                                  setShowProjectDropdown(false);
                                }}
                                className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm text-gray-700"
                              >
                                {site?.site_name}
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  <div ref={vendorRef}>
                    <div className="flex flex-row items-center mr-3 justify-between">
                      <div className="text-sm text-[#232323] mb-1">Vendor</div>
                      <span
                        className="text-blue-600 text-xs cursor-pointer"
                        onClick={() => setVendor(null)}
                      >
                        Clear
                      </span>
                    </div>
                    <div
                      onClick={() => setShowVendorDropdown(!showVendorDropdown)}
                      className="h-10 px-3 cursor-pointer bg-gray-200 rounded-md text-sm text-[#232323] flex items-center justify-between"
                    >
                      {vendor?.name || "Select Vendor"}
                      <span className="text-[#232323]">
                        <ChevronDown size={18} />
                      </span>
                    </div>
                    {showVendorDropdown && (
                      <div className="relative mt-1 w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto">
                        {vendors.length === 0 ? (
                          <div className="p-4 text-sm text-gray-500 text-center">
                            No vendors available
                          </div>
                        ) : (
                          vendors.map((v) => (
                            <div
                              key={v.id}
                              onClick={() => {
                                setVendor(v);
                                setShowVendorDropdown(false);
                              }}
                              className="px-4 py-2.5 hover:bg-blue-50 cursor-pointer text-sm text-gray-800 border-b border-gray-100 last:border-b-0 transition-colors duration-150"
                            >
                              <div className="font-medium">{v?.name}</div>
                              <div className="text-xs text-gray-500 mt-0.5">
                                {v?.contact_no}
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                  {!isWarehouse && (
                    <div ref={mrRef}>
                      <div className="flex items-center mr-3 justify-between">
                        <div className="text-sm text-[#232323] mb-1">
                          Link Material Request{" "}
                          <span className="text-gray-400">(Optional)</span>
                        </div>
                        <span
                          className="text-blue-600 text-xs cursor-pointer"
                          onClick={() => setSelectedMR(null)}
                        >
                          Clear
                        </span>
                      </div>
                      {/* <div
                        onClick={() => setShowMRDropdown(!showMRDropdown)}
                        className="h-10 px-3 bg-gray-200 rounded-md text-sm text-[#232323] flex items-center justify-between"
                      > */}
                      <div
                        onClick={() =>
                          !selectedMR && setShowMRDropdown(!showMRDropdown)
                        }
                        className={`h-10 px-3 bg-gray-200 rounded-md text-sm text-[#232323] flex items-center justify-between ${selectedMR
                            ? "cursor-not-allowed opacity-75"
                            : "cursor-pointer"
                          }`}
                      >
                        {selectedMR?.mr_number || "Select Material Request"}
                        {/* <span className="text-[#232323]">
                          <ChevronDown size={18} />
                        </span> */}
                      </div>
                      <div
                        className={`${showMRDropdown ? "block" : "hidden"
                          } relative mt-1 w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto`}
                      >
                        {requests.length === 0 ? (
                          <div className="p-4 text-sm text-gray-500 text-center">
                            No open material requests available
                          </div>
                        ) : (
                          requests.map((r) => (
                            <div
                              key={r.id}
                              onClick={() => handleMaterialRequestSelect(r)}
                              className="px-4 py-2.5 hover:bg-blue-50 cursor-pointer text-sm text-gray-800 border-b border-gray-100 last:border-b-0 transition-colors duration-150"
                            >
                              <div className="font-medium">{r?.mr_number}</div>
                              <div className="text-xs text-gray-500 mt-0.5">
                                {r?.items} items - Expected by{" "}
                                {formatDisplayDate(r?.due_date)}
                              </div>
                              <div className="text-xs text-blue-700 mt-0.5">
                                <span className="underline">
                                  {r?.site_name}
                                </span>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                  {linkedRfq && (
                    <div ref={quotationRef}>
                      <div className="flex items-center justify-between pr-3">
                        <div className="text-sm text-[#232323] mb-1">
                          Link a received Quotation{" "}
                          <span className="text-gray-400">(Optional)</span>
                        </div>
                        <span
                          className="text-blue-600 text-xs cursor-pointer"
                          onClick={() => setSelectedQuotation(null)}
                        >
                          Clear
                        </span>
                      </div>
                      <div
                        onClick={() =>
                          setShowQuotationDropdown(!showQuotationDropdown)
                        }
                        className="h-10 px-3 bg-gray-200 rounded-md text-sm text-[#232323] flex items-center justify-between"
                      >
                        {selectedQuotation?.quotation_number ||
                          "Select Quotation"}
                        <span className="text-[#232323]">
                          <ChevronDown size={18} />
                        </span>
                      </div>
                      {showQuotationDropdown && (
                        <div className="relative mt-1 w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto">
                          {quotations.length === 0 ? (
                            <div className="p-4 text-sm text-gray-500 text-center">
                              No Quotations available
                            </div>
                          ) : (
                            quotations.map((q) => (
                              <div
                                key={q.id}
                                onClick={() => handleQuotationSelect(q)}
                                className="px-4 py-2.5 hover:bg-blue-50 cursor-pointer text-sm text-gray-800 border-b border-gray-100 last:border-b-0 transition-colors duration-150"
                              >
                                <div className="font-medium">
                                  {q?.quotation_number}
                                </div>
                                <div className="text-xs text-gray-500 mt-0.5">
                                  {q?.application_lines?.length || 0} items
                                  {q?.rfq_number ? `:- ${q.rfq_number}` : ""}
                                  {" • "}
                                  {formatDisplayDate(q?.created_on)}
                                </div>
                                <div className="text-xs text-gray-600 mt-0.5">
                                  <span className="font-medium">Vendor:</span>{" "}
                                  <span className="text-blue-700 underline">
                                    {q?.vendor_name || "N/A"}
                                  </span>
                                </div>
                                <div className="text-xs text-gray-700 font-semibold mt-1">
                                  Total: ₹
                                  {q?.total_price?.toLocaleString("en-IN", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  }) || "0.00"}
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  <div>
                    <div className="text-sm text-[#232323] mb-1">
                      Select Delivery Date
                    </div>

                    <input
                      type="date"
                      value={deliveryDate}
                      disabled={selectedQuotation}
                      onChange={(e) => {
                        setDeliveryDate(e.target.value);
                        setShowDatePicker(false);
                      }}
                      className="h-10 px-3 w-full bg-gray-200 rounded-md text-sm text-[#232323] flex items-center justify-between"
                    />
                  </div>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="text-sm font-medium text-gray-900">
                      Materials
                    </div>
                    <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-gray-200 text-gray-600">
                      {materials.length}
                    </span>
                    <button
                      onClick={addMaterial}
                      disabled={selectedQuotation || selectedMR || linkedRfq}
                      className="ml-auto w-7 h-7 rounded-full border border-gray-300 text-gray-700 flex items-center justify-center hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      aria-label="Add material"
                    >
                      <FaPlus size={12} />
                    </button>
                  </div>

                  <div className="space-y-4">
                    {materials.map((m, idx) => (
                      <div
                        key={idx}
                        className="space-y-3 p-3 border rounded-lg bg-gray-50"
                      >
                        <div className="flex items-center gap-2">
                          <div className="relative w-full">
                            <select
                              value={m.id || ""}
                              disabled={
                                selectedQuotation || selectedMR || linkedRfq
                              }
                              onChange={(e) => {
                                const selected = materialLibrary.find(
                                  (mat) => mat.id === Number(e.target.value),
                                );
                                if (selected) {
                                  updateMaterial(idx, {
                                    id: selected.id,
                                    name: selected.material_name,
                                    specification: selected.specification,
                                    subcategory: selected.subcategory,
                                    unit: selected.uom,
                                    brand: selected.brand_name,
                                  });
                                }
                              }}
                              className="h-10 px-3 bg-white rounded-md w-full text-sm appearance-none border border-gray-300"
                            >
                              <option value="">Select Material</option>
                              {materialLibrary.map((mat) => (
                                <option key={mat.id} value={mat.id}>
                                  {mat.material_name} ({mat.specification},{" "}
                                  {mat.subcategory})
                                </option>
                              ))}
                            </select>

                            <ChevronDown
                              size={18}
                              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-600 pointer-events-none"
                            />
                          </div>
                          <button
                            onClick={() => removeMaterial(idx)}
                            disabled={
                              selectedQuotation || selectedMR || linkedRfq
                            }
                            className="text-red-500 hover:text-red-700 p-2 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>

                        <div className="grid grid-cols-12 gap-2 items-center">
                          <div className="col-span-4">
                            <input
                              type="text"
                              inputMode="decimal"
                              placeholder="Quantity"
                              value={m.qty}
                              disabled={
                                selectedQuotation || selectedMR || linkedRfq
                              }
                              onChange={(e) => {
                                const sanitized = e.target.value
                                  .replace(/[^0-9.]/g, "")
                                  .replace(/(\..*)\./g, "$1");
                                updateMaterial(idx, { qty: sanitized });
                              }}
                              className="h-10 px-3 bg-white border border-gray-300 rounded-md w-full text-sm"
                            />
                          </div>
                          <div className="col-span-3 relative">
                            <input
                              value={m.unit}
                              disabled={true}
                              placeholder="Unit"
                              className="h-10 px-3 bg-gray-100 border border-gray-300 rounded-md w-full text-sm appearance-none"
                            />
                          </div>
                          <div className="col-span-1 flex items-center justify-center">
                            <X size={16} className="text-gray-500" />
                          </div>
                          <div className="col-span-4 relative">
                            <IndianRupee className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 w-4 h-4" />
                            <input
                              type="text"
                              inputMode="decimal"
                              placeholder="Rate"
                              value={m.rate}
                              disabled={selectedQuotation}
                              onChange={(e) => {
                                const sanitized = e.target.value
                                  .replace(/[^0-9.]/g, "")
                                  .replace(/(\..*)\./g, "$1");
                                const numericRate = parseFloat(sanitized) || 0;
                                updateMaterial(idx, {
                                  rate: sanitized,
                                  total_price:
                                    parseFloat(m.qty || 0) * numericRate +
                                    parseFloat(m.tax_amount || 0),
                                });
                              }}
                              className="h-10 pl-8 pr-3 bg-white border border-gray-300 rounded-md w-full text-sm text-right"
                            />
                          </div>
                        </div>

                        {m.showGstInput && (
                          <div className="grid grid-cols-2 gap-3 pt-2">
                            <div>
                              <label className="text-xs text-gray-600 mb-1 block">
                                GST %
                              </label>
                              <select
                                value={m.gst_percentage}
                                onChange={(e) =>
                                  updateMaterial(idx, {
                                    gst_percentage: parseFloat(e.target.value),
                                    tax_amount:
                                      (parseFloat(e.target.value) / 100) *
                                      (m.qty * m.rate),
                                    total_price:
                                      m.qty * m.rate +
                                      m.qty *
                                      m.rate *
                                      (parseFloat(e.target.value) / 100),
                                  })
                                }
                                disabled={selectedQuotation}
                                className="h-9 px-3 bg-white border border-gray-300 rounded-md w-full text-sm"
                              >
                                {GST_SLABS.map((slab) => (
                                  <option key={slab} value={slab}>
                                    {slab}%
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="text-xs text-gray-600 mb-1 block">
                                Tax Amount
                              </label>
                              <div className="relative">
                                <IndianRupee className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 w-3 h-3" />
                                <input
                                  type="text"
                                  value={m.tax_amount.toFixed(2)}
                                  className="h-9 pl-7 pr-3 bg-gray-100 border border-gray-300 rounded-md w-full text-sm text-right"
                                />
                              </div>
                            </div>
                          </div>
                        )}

                        <div className="flex justify-between items-center pt-2">
                          <button
                            onClick={() => toggleGstInput(idx)}
                            disabled={selectedQuotation}
                            className="px-3 py-1.5 text-xs bg-[#ECE6F0] border border-transparent rounded-full text-[#3B0069] hover:bg-purple-200 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {m.showGstInput ? "Remove GST" : "Add GST"}
                          </button>
                          <div className="text-sm">
                            Total Price{" "}
                            <span className="font-semibold">
                              ₹{" "}
                              {materialTotals[idx].toLocaleString("en-IN", {
                                maximumFractionDigits: 2,
                              })}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {materials.length > 0 && (
                    <div className="mt-4 pt-4 border-t flex justify-end">
                      <div className="text-base font-semibold">
                        Grand Total:{" "}
                        <span className="text-[#3B0069]">
                          ₹{" "}
                          {grandTotal.toLocaleString("en-IN", {
                            maximumFractionDigits: 2,
                          })}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="text-sm text-gray-900 flex items-center justify-between border-t pt-3 mt-3">
                  <span>Grand Total</span>
                  <span className="font-semibold">
                    ₹ {grandTotal.toLocaleString()}
                  </span>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center gap-4 text-sm">
                    <p className="flex-shrink-0">Payment Method</p>
                    <div className="flex gap-2">
                      {["Cash", "Online", "Cheque"].map((method) => (
                        <button
                          key={method}
                          onClick={() => setPaymentMethod(method)}
                          className={`px-4 py-1 rounded-full text-sm font-medium ${paymentMethod === method
                              ? "bg-[#ECE6F0] text-[#3B0069] border-1 border-[#3B0069]"
                              : "bg-gray-200 text-gray-700"
                            }`}
                        >
                          {method}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <p className="flex-shrink-0">Payment Status</p>
                    <div className="flex gap-2">
                      {["Partial Paid", "Full Paid", "Due"].map((status) => (
                        <button
                          key={status}
                          onClick={() => {
                            setPaymentStatus(status);
                            if (status === "Full Paid") {
                              setPaidAmount(grandTotal);
                            }
                            if (status === "Due") {
                              setPaidAmount(0);
                            }
                          }}
                          className={`px-4 py-1 rounded-full text-sm font-medium ${paymentStatus === status
                              ? "bg-[#ECE6F0] text-[#3B0069] border-1 border-[#3B0069]"
                              : "bg-gray-200 text-gray-700"
                            }`}
                        >
                          {status}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-[#232323] mb-1">
                      Paid Amount
                    </div>
                    <div className="h-10 px-3 bg-gray-200 rounded-md flex items-center text-sm text-gray-700 relative">
                      <span className="mr-2">₹</span>
                      <input
                        type="text"
                        inputMode="decimal"
                        disabled={
                          paymentStatus === "Full Paid" ||
                          paymentStatus === "Due"
                        }
                        placeholder="Amount"
                        value={paidAmount}
                        // onChange={(e) => setPaidAmount(e.target.value)}
                        onChange={(e) => {
                          const sanitized = e.target.value
                            .replace(/[^0-9.]/g, "")
                            .replace(/(\..*)\./g, "$1");
                          const numeric = parseFloat(sanitized) || 0;
                          // Cap at grand total — partial paid cannot exceed total bill
                          setPaidAmount(
                            numeric > grandTotal ? grandTotal : sanitized,
                          );
                        }}
                        className={`bg-transparent w-full outline-none ${paymentStatus === "Full Paid" ||
                            paymentStatus === "Due"
                            ? "cursor-not-allowed opacity-75"
                            : ""
                          }`}
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <div className="text-[#232323]">Remaining Payment</div>
                    <div className="text-gray-900 font-medium">
                      ₹ {remainingPayment.toLocaleString()}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-[#232323] mb-1">
                      Remarks (Optional)
                    </div>
                    <div className="h-10 px-3 bg-gray-200 rounded-md flex items-center text-sm text-gray-700">
                      <input
                        placeholder="Type here"
                        value={remarks}
                        onChange={(e) => setRemarks(e.target.value)}
                        className="bg-transparent w-full outline-none"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-shrink-0 pt-2">
        <div className="flex items-center justify-center gap-3">
          {/* ─── Button brightens ONLY when every required field is filled ─── */}
          <button
            onClick={handleApprove}
            disabled={!isFormValid}
            className={`min-w-[220px] h-11 inline-flex items-center justify-center rounded-lg bg-[#3B0069] text-white text-sm font-medium px-6 transition-opacity duration-200 ${!isFormValid ? "opacity-50 cursor-not-allowed" : "opacity-100"
              }`}
          >
            Approve &amp; Purchase
          </button>
        </div>
      </div>

      {showAIModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Create PO from Bill (AI)
              </h3>
              <button
                onClick={() => {
                  setShowAIModal(false);
                  setBillDocument(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload Bill Document
                </label>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => setBillDocument(e.target.files[0])}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Supported formats: PDF, JPG, PNG
                </p>
              </div>

              {billDocument && (
                <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                  <strong>Selected:</strong> {billDocument.name}
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowAIModal(false);
                    setBillDocument(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAIBillProcess}
                  disabled={!billDocument || isProcessingBill}
                  className="flex-1 px-4 py-2 bg-[#3B0069] text-white rounded-lg hover:bg-purple-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isProcessingBill ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Processing...
                    </>
                  ) : (
                    "Process Bill"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {unmatchedItems.length > 0 && (
        <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-yellow-800">
              Unmatched Items ({unmatchedItems.length})
            </h4>
            <button
              onClick={() => setUnmatchedItems([])}
              className="text-xs text-yellow-600 hover:text-yellow-800"
            >
              Dismiss
            </button>
          </div>
          <p className="text-xs text-yellow-700 mb-3">
            These items from the bill were not found in your material library.
            Add them to create complete PO.
          </p>
          <div className="space-y-2">
            {unmatchedItems.map((item, idx) => (
              <div
                key={idx}
                className="flex items-center justify-between bg-white p-3 rounded-md border border-yellow-100"
              >
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900">
                    {item.name}
                  </div>
                  {item.specification && (
                    <div className="text-xs text-gray-500">
                      {item.specification}
                    </div>
                  )}
                  <div className="text-xs text-gray-600 mt-1">
                    Qty: {item.quantity} {item.unit} • Rate: ₹{item.unit_price}
                  </div>
                </div>
                <button
                  onClick={() => {
                    console.log("Add to library:", item);
                  }}
                  className="ml-3 px-3 py-1.5 bg-[#3B0069] text-white text-xs rounded-lg hover:bg-purple-800 whitespace-nowrap"
                >
                  Add to Library
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
